import { useEffect, useRef } from "react";

const TAB_CHANNEL_NAME = "tab";
const TAB_STORAGE_KEY = "active-tab-id";
const TAB_TIMESTAMP_KEY = "active-tab-timestamp";

/**
 * Hook that enforces single-tab behavior.
 * When a new tab opens, all existing tabs will close themselves.
 * The newest tab always wins.
 *
 * Note: This hook does NOT enforce single-tab behavior when running inside an iframe.
 */
export const useSingleTab = () => {
  const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random()}`);
  const tabTimestampRef = useRef<number>(Date.now());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isClosingRef = useRef<boolean>(false);

  useEffect(() => {
    // Check if we're inside an iframe - if so, don't enforce single-tab behavior
    const isInsideIframe =
      window.self !== window.top || window.frameElement !== null;
    if (isInsideIframe) {
      // We're inside an iframe, skip single-tab enforcement
      return;
    }

    const tabId = tabIdRef.current;
    const tabTimestamp = tabTimestampRef.current;

    // Create BroadcastChannel
    const channel = new BroadcastChannel(TAB_CHANNEL_NAME);
    channelRef.current = channel;

    // Function to close this tab immediately
    const closeTab = () => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;

      // Immediately redirect to about:blank (more reliable than window.close)
      window.location.href = "about:blank";
    };

    // Listen for new tab notifications
    channel.onmessage = (event) => {
      if (event.data && event.data.type === "NEW_TAB") {
        const newTabTimestamp = event.data.timestamp;
        const newTabId = event.data.tabId;

        // If the new tab is newer than us, close this tab
        if (newTabId !== tabId && newTabTimestamp > tabTimestamp) {
          closeTab();
        }
      }
    };

    // Check if there's already an active tab
    const previousTabId = localStorage.getItem(TAB_STORAGE_KEY);
    const previousTimestamp = localStorage.getItem(TAB_TIMESTAMP_KEY);
    const previousTimestampNum = previousTimestamp
      ? parseInt(previousTimestamp, 10)
      : 0;

    // If there's a previous tab and it's older, we win - claim the tab
    // If there's a previous tab and it's newer, we lose - close this tab
    if (previousTabId && previousTabId !== tabId) {
      if (tabTimestamp > previousTimestampNum) {
        // We're newer - notify the old tab to close and claim the tab
        channel.postMessage({
          type: "NEW_TAB",
          tabId: tabId,
          timestamp: tabTimestamp,
        });
        localStorage.setItem(TAB_STORAGE_KEY, tabId);
        localStorage.setItem(TAB_TIMESTAMP_KEY, tabTimestamp.toString());
      } else {
        // We're older - close this tab
        closeTab();
        return;
      }
    } else {
      // This is the first tab - claim it
      localStorage.setItem(TAB_STORAGE_KEY, tabId);
      localStorage.setItem(TAB_TIMESTAMP_KEY, tabTimestamp.toString());

      // Notify any other tabs that might be opening
      channel.postMessage({
        type: "NEW_TAB",
        tabId: tabId,
        timestamp: tabTimestamp,
      });
    }

    // Listen to storage events as backup
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TAB_STORAGE_KEY && e.newValue && e.newValue !== tabId) {
        // Check timestamp
        const newTimestamp = localStorage.getItem(TAB_TIMESTAMP_KEY);
        const newTimestampNum = newTimestamp ? parseInt(newTimestamp, 10) : 0;

        if (newTimestampNum > tabTimestamp) {
          // Another tab is newer, close this one
          closeTab();
        }
      } else if (e.key === TAB_TIMESTAMP_KEY && e.newValue) {
        const newTimestamp = parseInt(e.newValue, 10);
        const currentTabId = localStorage.getItem(TAB_STORAGE_KEY);

        if (currentTabId !== tabId && newTimestamp > tabTimestamp) {
          // Another tab is newer, close this one
          closeTab();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Periodically check if we're still the active tab
    const heartbeatInterval = setInterval(() => {
      const currentActiveTabId = localStorage.getItem(TAB_STORAGE_KEY);
      const currentTimestamp = localStorage.getItem(TAB_TIMESTAMP_KEY);
      const currentTimestampNum = currentTimestamp
        ? parseInt(currentTimestamp, 10)
        : 0;

      if (currentActiveTabId && currentActiveTabId !== tabId) {
        if (currentTimestampNum > tabTimestamp) {
          // Another tab is newer, close this one
          closeTab();
          clearInterval(heartbeatInterval);
        }
      }
    }, 100); // Check every 100ms

    // Cleanup
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener("storage", handleStorageChange);
      channel.close();

      // Only remove from localStorage if this is still the active tab
      if (localStorage.getItem(TAB_STORAGE_KEY) === tabId) {
        localStorage.removeItem(TAB_STORAGE_KEY);
        localStorage.removeItem(TAB_TIMESTAMP_KEY);
      }
    };
  }, []);

  // Handle visibility change - if tab becomes visible, check if we should close
  useEffect(() => {
    // Check if we're inside an iframe - if so, don't enforce single-tab behavior
    const isInsideIframe =
      window.self !== window.top || window.frameElement !== null;
    if (isInsideIframe) {
      // We're inside an iframe, skip single-tab enforcement
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isClosingRef.current) {
        const currentTabId = localStorage.getItem(TAB_STORAGE_KEY);
        const currentTimestamp = localStorage.getItem(TAB_TIMESTAMP_KEY);
        const currentTimestampNum = currentTimestamp
          ? parseInt(currentTimestamp, 10)
          : 0;

        if (currentTabId && currentTabId !== tabIdRef.current) {
          if (currentTimestampNum > tabTimestampRef.current) {
            // Another tab is newer, close this one
            isClosingRef.current = true;
            window.location.href = "about:blank";
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
